import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { useState } from "react"

const formSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  phone: z.string().optional(),
})

interface EventRegistrationFormProps {
  eventId: string
  onSuccess?: () => void
}

export const EventRegistrationForm = ({ eventId, onSuccess }: EventRegistrationFormProps) => {
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
    },
  })

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (isSubmitting) return
    
    setIsSubmitting(true)
    try {
      const { error } = await supabase
        .from("event_participants")
        .insert({
          name: data.name,
          email: data.email,
          phone: data.phone || null,
          event_id: eventId,
          status: "signed_up" as const,
        })

      if (error) {
        // Check if this is a unique constraint violation
        if (error.code === '23505') {
          toast({
            title: "Already registered",
            description: "You have already registered for this event with this email address.",
            variant: "destructive",
          })
          return
        }
        throw error
      }

      const { data: emailResponse, error: emailError } = await supabase.functions.invoke(
        "send-registration-email",
        {
          body: JSON.stringify({
            eventId,
            participantName: data.name,
            participantEmail: data.email,
          }),
        }
      )

      if (emailError) {
        console.error("Error sending confirmation email:", emailError)
        // Don't throw here, we still want to show success message for registration
      }

      toast({
        title: "Registration successful!",
        description: "You have been registered for this event. Check your email for confirmation.",
      })

      form.reset()
      onSuccess?.()
    } catch (error: any) {
      toast({
        title: "Registration failed",
        description: error.message || "Please try again later.",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Full name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone Number (Optional)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1234567890" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? "Registering..." : "Register for Event"}
        </Button>
      </form>
    </Form>
  )
}